/**
 * reviewDocuments — the pure, dependency-free scoping/merge/display core for the
 * Application_Documents_View (application-review-documents spec).
 *
 * This is the single place the review surface expresses season scoping, identity
 * scoping, union-by-id, and Display_Type resolution, so the review view and the
 * Community record Documents section cannot diverge (R6.2, R6.4). It reuses the
 * shared identity-set primitives (`mergeDocumentReferencesById`,
 * `distinctApplicantIds`, `ApplicantIdentitySet`) from `applicantIdentity.ts`
 * rather than re-implementing the union.
 *
 * Everything here is pure and input-varying — the prime property-based-testing
 * surface for this feature. All I/O lives in the `useReviewDocuments` hook.
 */

import {
  distinctApplicantIds,
  mergeDocumentReferencesById,
  type ApplicantIdentitySet,
} from '@/lib/application-form/identity/applicantIdentity';

/** The minimal DocumentReference projection the review pipeline consumes. */
export interface RawDocumentReference {
  id: string;
  applicantId: string;
  documentType: string;
  uploadedAt?: string | null;
  fileName?: string | null;
  catalogEntryId?: string | null;
  /** competitionYear when the row carries it; used by the fail-closed season guard. */
  competitionYear?: number | null;
}

/** One rendered document (post scope + label overlay). */
export interface ReviewDocument {
  id: string;
  applicantId: string;
  /** Resolved Display_Type: catalog label, else humanized documentType. Never blank (R3.1). */
  displayType: string;
  /** Original filename, or undefined → the view renders a placeholder (R3.9). */
  fileName?: string;
  /** uploadedAt, or undefined → the view renders a placeholder (R3.9). */
  uploadedAt?: string;
  /**
   * Verified marker for structural parity with the Community record. Always
   * false on the current data path (Decision D3, R3.6 no-op) — not surfaced.
   */
  verified: boolean;
}

/**
 * True iff `year` is a usable competition year (a positive integer). Fail-closed
 * type guard (R2.1 belt-and-braces): null, undefined, non-integer, NaN, and
 * values ≤ 0 all resolve to false.
 */
export function isWellFormedYear(year: number | null | undefined): year is number {
  return typeof year === 'number' && Number.isInteger(year) && year > 0;
}

/**
 * Humanize a raw `DocumentType` enum (e.g. `HEADSHOT`, `APPLICATION_DOCUMENT`)
 * into a readable Title-Case label ("Headshot", "Application Document"). Used as
 * the Display_Type fallback when a DocumentReference is not linked to a readable
 * catalog entry.
 */
export function humanizeDocumentType(raw: string): string {
  return raw
    .split('_')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Resolve one document's Display_Type: the director-configured catalog `label`
 * when the reference is linked to a catalog entry we could read, else a
 * humanized form of its `documentType` enum (R3.1, R3.2, R3.3). Never blank.
 */
export function resolveDisplayType(
  documentType: string,
  catalogEntryId: string | null | undefined,
  catalogLabels: ReadonlyMap<string, string>,
): string {
  const label = catalogEntryId ? catalogLabels.get(catalogEntryId) : undefined;
  return label ?? humanizeDocumentType(documentType);
}

/**
 * Resolve one document's display Type: the director-configured catalog label
 * when the reference is linked to a catalog entry we could read, else a
 * humanized form of its `documentType` enum.
 *
 * This is the canonical home for the helper extracted verbatim from
 * `usePersonRecord.ts` (application-review-documents R6.1, R7.4): there is
 * exactly one implementation, and `usePersonRecord` imports it from here so its
 * observable output stays byte-identical.
 */
export function resolveDocumentType(
  rawType: string,
  catalogEntryId: string | undefined,
  catalogLabels: Map<string, string>,
): string {
  const label = catalogEntryId ? catalogLabels.get(catalogEntryId) : undefined;
  return label ?? humanizeDocumentType(rawType);
}

/**
 * Keep only references whose `applicantId` is a member of the reviewed
 * contestant's identity set AND (when the row carries a `competitionYear`) whose
 * year is well-formed and exactly equals the reviewed year; exclude otherwise
 * (fail-closed, R2.1, R2.6).
 *
 * The year-scoped `listDocumentReferences(applicantId, competitionYear)` query is
 * the primary season guard; this is the belt-and-braces client-side net. A row
 * that carries a malformed or non-matching year is dropped; a row that carries no
 * year at all is scoped by identity only (the query already bound the season).
 */
export function scopeDocuments(
  refs: readonly RawDocumentReference[],
  identity: ApplicantIdentitySet,
  reviewedYear: number,
): RawDocumentReference[] {
  const allowedIds = new Set(distinctApplicantIds(identity));
  return refs.filter((ref) => {
    if (!allowedIds.has(ref.applicantId)) return false;
    // Only apply the year guard when the row carries a competitionYear.
    if (ref.competitionYear != null) {
      if (!isWellFormedYear(ref.competitionYear)) return false;
      if (ref.competitionYear !== reviewedYear) return false;
    }
    return true;
  });
}

/** Normalize a possibly-blank string to `undefined` (R3.9 placeholder input). */
function blankToUndefined(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  return value.trim() === '' ? undefined : value;
}

/**
 * The full pure pipeline: union-by-id → scope (season + identity) → overlay
 * Display_Type → project to `ReviewDocument`. Given the same identity set,
 * reviewed year, reference rows, and catalog map, produces a set whose
 * membership-by-id and per-document Display_Type match the Community record
 * (R6.2, R6.4). Order-independent by design (R6.3).
 *
 * `mergeDocumentReferencesById(...legs)` dedupes cross-leg rows (a document keyed
 * under both the durable `personId` and the legacy `Applicant.id` appears once)
 * and never drops a single-space row (R2.5).
 */
export function composeReviewDocuments(input: {
  identity: ApplicantIdentitySet;
  reviewedYear: number;
  legs: ReadonlyArray<readonly RawDocumentReference[]>;
  catalogLabels: ReadonlyMap<string, string>;
}): ReviewDocument[] {
  const { identity, reviewedYear, legs, catalogLabels } = input;
  const merged = mergeDocumentReferencesById(...legs);
  const scoped = scopeDocuments(merged, identity, reviewedYear);
  return scoped.map((ref) => ({
    id: ref.id,
    applicantId: ref.applicantId,
    displayType: resolveDisplayType(ref.documentType, ref.catalogEntryId, catalogLabels),
    fileName: blankToUndefined(ref.fileName),
    uploadedAt: blankToUndefined(ref.uploadedAt),
    verified: false,
  }));
}
